import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../Header/Header';
import Sidebar from '../Sidebar/Sidebar';
import ErrorBoundary from '../../Common/ErrorBoundary/ErrorBoundary';
import { checkSession } from '../../../store/slices/authSlice';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import useGlobalChatSocket from '../../../hooks/useGlobalChatSocket';
import './Layout.css';

// AiAssistantWidget is mounted once in App.jsx (not here) so chat survives route changes.

const LAST_VISITED_KEY = 'lastVisitedPath';

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, sessionExpired } = useSelector((state) => state.auth);
  const { updateBreadcrumbs } = useBreadcrumb();
  useGlobalChatSocket(user);
  const [sidebarOpen] = useState(true);

  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 1199;
  });



  // Periodic session check (every 5 minutes)
  useEffect(() => {
    if (!user) return;

    const sessionCheckInterval = setInterval(() => {
      dispatch(checkSession({ isBackground: true }));
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => {
      clearInterval(sessionCheckInterval);
    };
  }, [user, dispatch]);

  // Handle session timeout removed - centralized in App.jsx

  // Sidebar stays permanently visible (no hamburger toggle)
  const toggleSidebar = () => {};
  const closeSidebar = () => {};

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 1199);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (location.pathname === '/login') {
      sessionStorage.removeItem(LAST_VISITED_KEY);
      return;
    }
    const composedPath = `${location.pathname}${location.search}${location.hash}`;
    sessionStorage.setItem(LAST_VISITED_KEY, composedPath);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isChatRoute =
      location.pathname === '/chat' || location.pathname.startsWith('/chat/');
    if (isChatRoute) {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [location.pathname, location.search]);

  const layoutClasses = [
    'mainsection',
    'sidebar-open',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={layoutClasses}>
      {/* Header */}
      <Header
        user={user}
        onToggleSidebar={toggleSidebar}
        sidebarOpen={sidebarOpen}
      />

      <Sidebar
        user={user}
        isOpen={true}
        onClose={closeSidebar}
        isMobileView={isMobileView}
      />

      {/* Main Content */}
      <div className="right-panel">
        <div
          className={`main-content${location.pathname === '/chat' || location.pathname.startsWith('/chat/') ? ' main-content--chat' : ''}`}
        >
          <ErrorBoundary>
            {children}
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
};

export default Layout;
